import { createFileRoute, Link } from "@tanstack/react-router";
import { Wallet, CalendarClock, Globe2, Headphones, Smartphone, Navigation, ClipboardList } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/for-suppliers")({
  head: () => ({
    meta: [
      { title: "Become a Driver in Morocco | Bacha Way Travel Network" },
      { name: "description", content: "Join the Bacha Way driver network. Earn driving international travellers across Morocco — flexible hours, weekly payouts, 24/7 support." },
      { property: "og:title", content: "Become a Driver — Bacha Way Travel" },
      { property: "og:description", content: "Join the chauffeur network. Flexible hours, international clientele, weekly payouts." },
      { property: "og:url", content: "https://bachawaytravel.com/for-suppliers" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/for-suppliers" }],
  }),
  component: ForSuppliersPage,
});

function ForSuppliersPage() {
  const { t } = useTranslation();
  const benefits = [
    { icon: Wallet, title: t("suppliers.b1t"), desc: t("suppliers.b1d") },
    { icon: CalendarClock, title: t("suppliers.b2t"), desc: t("suppliers.b2d") },
    { icon: Globe2, title: t("suppliers.b3t"), desc: t("suppliers.b3d") },
    { icon: Headphones, title: t("suppliers.b4t"), desc: t("suppliers.b4d") },
  ];
  const steps = [
    { n: "01", title: t("suppliers.s1t"), desc: t("suppliers.s1d") },
    { n: "02", title: t("suppliers.s2t"), desc: t("suppliers.s2d") },
    { n: "03", title: t("suppliers.s3t"), desc: t("suppliers.s3d") },
  ];
  const appFeatures = [
    { icon: ClipboardList, title: t("suppliers.a1t"), desc: t("suppliers.a1d") },
    { icon: Smartphone, title: t("suppliers.a2t"), desc: t("suppliers.a2d") },
    { icon: Navigation, title: t("suppliers.a3t"), desc: t("suppliers.a3d") },
  ];
  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80">
            {t("suppliers.badge")}
          </div>
          <h1 className="mt-8 mx-auto max-w-3xl text-4xl md:text-6xl font-bold leading-[1.05]">
            {t("suppliers.title1")} <span className="text-gradient-sky">{t("suppliers.title2")}</span>
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-white/70">{t("suppliers.lead")}</p>
          <Link to="/contact" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary">{t("suppliers.signup")}</Link>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("suppliers.whyEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("suppliers.whyTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-border bg-card p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><b.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{b.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("suppliers.howEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("suppliers.howTitle")}</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-card p-8">
                <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("suppliers.step")} {s.n}</div>
                <div className="mt-3 text-2xl font-bold">{s.title}</div>
                <p className="mt-3 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("suppliers.appEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("suppliers.appTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {appFeatures.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-7 hover:border-primary/40 transition-colors">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><f.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{f.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-3xl bg-hero-navy text-white p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-5xl font-bold">{t("suppliers.ctaTitle")}</h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">{t("suppliers.ctaLead")}</p>
            <Link to="/contact" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary">{t("suppliers.becomeDriver")}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
